import { alpha, Box, Card, Typography, useTheme } from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";
import {
    AreaChart,
    Area,
    ResponsiveContainer,
} from "recharts";

type TrendCardProps = {
    title: string;
    value: string;
    data: number[];
    color: "success" | "error" | "warning";
    trend?: "up" | "down";
};

export const MetricTrendCard: React.FC<TrendCardProps> = ({
    title,
    value,
    data,
    color,
    trend = "down",
}) => {
    const theme = useTheme();

    const chartData = data.map((v, i) => ({
        month: i,
        value: v,
    }));

    const chartColor = theme.palette[color].main;
    const trendColor = trend === "up" ? theme.palette.success.main : theme.palette.error.main;

    return (
        <Card variant="outlined" sx={{ p: 0, height: "100%", overflow: "hidden" }}>
            <Box sx={{ p: 2, pb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                        {title}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            bgcolor: alpha(trendColor, 0.1),
                            color: trendColor,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                        }}
                    >
                        {trend === "up" ? (
                            <TrendingUp sx={{ fontSize: 14 }} />
                        ) : (
                            <TrendingDown sx={{ fontSize: 14 }} />
                        )}
                    </Box>
                </Box>
                <Typography variant="h5" fontWeight={700} mt={0.5}>
                    {value}
                </Typography>
            </Box>

            <Box sx={{ width: "100%", height: 60 }}>
                <ResponsiveContainer>
                    <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id={`gradient-${title.replace(/\s/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke={chartColor}
                            strokeWidth={2}
                            fill={`url(#gradient-${title.replace(/\s/g, '')})`}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Card>
    );
};